import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const ChangePassword: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setErrorMessage('');
        setSuccessMessage('');

        // Client-side validation
        if (!currentPassword || !newPassword || !confirmPassword) {
            setErrorMessage(t('password.allFieldsRequired'));
            setSaving(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMessage(t('password.passwordsNotMatch'));
            setSaving(false);
            return;
        }

        if (newPassword.length < 6) {
            setErrorMessage(t('password.passwordTooShort'));
            setSaving(false);
            return;
        }

        try {
            await api.put('/user/change-password', {
                current_password: currentPassword,
                new_password: newPassword,
                confirm_password: confirmPassword
            });
            setSuccessMessage(t('password.changeSuccess'));
            // Clear form
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            if (detail === 'current_password_incorrect') {
                setErrorMessage(t('password.currentPasswordIncorrect'));
            } else if (detail === 'passwords_not_match') {
                setErrorMessage(t('password.passwordsNotMatch'));
            } else if (detail === 'password_too_short') {
                setErrorMessage(t('password.passwordTooShort'));
            } else {
                setErrorMessage(t('password.changeFailed'));
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="dashboard-section mb-4">
            <div className="section-header">
                <h2 className="section-title">
                    <i className="bi bi-shield-lock me-2 text-warning"></i>
                    {t('password.title')}
                </h2>
            </div>
            <div className="section-body">
                {successMessage && (
                    <div className="alert alert-success alert-dismissible fade show">
                        <i className="bi bi-check-circle me-2"></i>
                        {successMessage}
                        <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
                    </div>
                )}
                {errorMessage && (
                    <div className="alert alert-danger alert-dismissible fade show">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {errorMessage}
                        <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">
                                {t('password.currentPassword')} <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="bi bi-key"></i>
                                </span>
                                <input
                                    type={showPasswords ? 'text' : 'password'}
                                    className="form-control"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <div className="col-md-4">
                            <label className="form-label">
                                {t('password.newPassword')} <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="bi bi-lock"></i>
                                </span>
                                <input
                                    type={showPasswords ? 'text' : 'password'}
                                    className="form-control"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <div className="col-md-4">
                            <label className="form-label">
                                {t('password.confirmPassword')} <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="bi bi-lock-fill"></i>
                                </span>
                                <input
                                    type={showPasswords ? 'text' : 'password'}
                                    className="form-control"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="showPasswords"
                                    checked={showPasswords}
                                    onChange={(e) => setShowPasswords(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="showPasswords">
                                    {t('password.showPasswords')}
                                </label>
                            </div>
                        </div>

                        <div className="col-12">
                            <button
                                type="submit"
                                className="btn btn-warning"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        {t('common.saving')}
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg me-2"></i>
                                        {t('password.changeButton')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </section>
    );
};

export default ChangePassword;
